"use client";

import { SearchX } from "lucide-react";
import HoverArrow from "@/components/shared/HoverArrow";
import LexikonCard from "@/components/lexikon/LexikonCard";
import { firstLetter, type LexikonTerm } from "@/lib/lexikon";

/** Gefilterte Begriffe, gruppiert nach Anfangsbuchstaben. */
export default function LexikonList({
  terms,
  onReset,
}: {
  terms: LexikonTerm[];
  onReset: () => void;
}) {
  if (terms.length === 0) {
    return (
      <div className="card-flat flex flex-col items-center gap-4 bg-white py-16 px-6 text-center">
        <SearchX size={28} className="text-ink-faint" aria-hidden="true" />
        <p className="text-ink-soft">
          Kein Begriff gefunden. Versuche einen anderen Suchbegriff oder Buchstaben.
        </p>
        <button
          onClick={onReset}
          className="group inline-flex items-center gap-2 px-6 py-3 rounded-full border border-line bg-white text-sm font-semibold text-ink hover:border-accent hover:text-accent transition-colors cursor-pointer"
        >
          Filter zurücksetzen
          <HoverArrow />
        </button>
      </div>
    );
  }

  // Nach Buchstaben gruppieren (Reihenfolge folgt der bereits sortierten Liste)
  const groups = new Map<string, LexikonTerm[]>();
  for (const term of terms) {
    const letter = firstLetter(term.term);
    const bucket = groups.get(letter) ?? [];
    bucket.push(term);
    groups.set(letter, bucket);
  }

  // Performance: only the first cards (the ones visible on load) get the
  // entrance animation, and every group but the first skips layout until it
  // scrolls near the viewport (.cv-auto) – the list renders ~50 cards at once.
  let animated = 0;
  return (
    <div className="flex flex-col gap-12">
      {[...groups.entries()].map(([letter, group], groupIndex) => (
        <section
          key={letter}
          id={`buchstabe-${letter}`}
          className={`scroll-mt-28 ${groupIndex > 0 ? "cv-auto [contain-intrinsic-size:auto_600px]" : ""}`}
        >
          <h2 className="mb-5 flex items-center gap-4">
            <span className="font-display font-bold text-3xl text-accent">{letter}</span>
            <span className="h-px flex-1 bg-line" aria-hidden="true" />
          </h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {group.map((term) => (
              <LexikonCard
                key={term.slug}
                term={term}
                index={animated < 6 ? animated++ : undefined}
              />
            ))}
          </div>
        </section>
      ))}
      <p className="sr-only">Ende der Liste</p>
    </div>
  );
}
